import {Button, ButtonGroup, closeDialog, Form, FormField, Separator} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';
import {z} from 'zod';

import {usePostStorageLocationAddLocationV2Mutation} from '@dms/api/core';
import {useGetSpacesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, refreshDmsDatagrid, WAREHOUSE_GRID_CODES} from '@dms/shared';

import {STORAGE_LOCATION_CONTEXT_TARGET} from '../constants/storageLocationContextTarget';

const DIALOG_ID = 'create-location-dialog';

const createLocationFormSchema = z.object({
  type: z.string(),
  space: z.string().min(1),
  location: z.string().min(1),
  note: z.string(),
});
type CreateLocationForm = z.infer<typeof createLocationFormSchema>;

interface CreateLocationDialogProps {
  spaceId: string;
  spaceName?: string;
  warehouseId: string;
}

const TYPE_OPTIONS = [
  {label: i18n.t('entity.warehouse.labels.simpleLocationType'), value: 'simple'},
];

export function CreateLocationDialog(props: CreateLocationDialogProps) {
  const [createLocation, createStatus] = usePostStorageLocationAddLocationV2Mutation();

  const {data: spaces} = useGetSpacesQuery({warehouseId: props.warehouseId});

  const spaceOptions = spaces
    ? spaces.map((space) => ({label: space.name, value: space.spaceId}))
    : [{label: props.spaceName ?? '', value: props.spaceId}];

  const handleClose = () => {
    closeDialog(DIALOG_ID);
  };

  const handleSubmit = async (formData: CreateLocationForm) => {
    await createLocation({
      contextTarget: STORAGE_LOCATION_CONTEXT_TARGET,
      contextId: formData.space,
      body: {
        location: formData.location,
        allowMultipleUsage: false,
        note: formData.note || undefined,
      },
    })
      .unwrap()
      .then(() => {
        refreshDmsDatagrid(WAREHOUSE_GRID_CODES.assignStorageLocationWarehouseSpace);
        handleClose();
      })
      .catch(handleApiError);
  };

  return (
    <Form
      defaultValues={{type: 'simple', space: props.spaceId, location: '', note: ''}}
      experimentalZodSchema={createLocationFormSchema}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <Grid columns={5}>
            <GridItem>
              <FormField
                type="radio"
                control={control}
                name="type"
                label={i18n.t('general.labels.type')}
                options={TYPE_OPTIONS}
              />
            </GridItem>
            <GridItem>
              <FormField
                type="choice"
                control={control}
                name="space"
                isNotClearable
                label={i18n.t('entity.warehouse.labels.space')}
                options={spaceOptions}
              />
            </GridItem>
          </Grid>
          <Separator />
          <FormField
            type="text"
            control={control}
            name="location"
            label={i18n.t('general.labels.name')}
            isRequired
          />
          <FormField
            type="textarea"
            control={control}
            name="note"
            label={i18n.t('general.labels.note')}
            isResizable
          />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={handleClose}
            />
            <Button
              title={i18n.t('general.actions.create')}
              type="submit"
              isLoading={createStatus.isLoading}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
